import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = ["input", "count", "clearBtn"]

    #matches = []
    #currentIndex = -1
    #highlight = null

    connect() {
        if (CSS.highlights) {
            this.#highlight = new Highlight()
            CSS.highlights.set("note-search", this.#highlight)
        }
        this.element.addEventListener("turbo:frame-load", this.#onFrameLoad)
        document.addEventListener("keydown", this.#onGlobalKeydown)
        this.#showClear(false)
    }

    disconnect() {
        this.#clearHighlights()
        if (CSS.highlights) {
            CSS.highlights.delete("note-search")
        }
        this.element.removeEventListener("turbo:frame-load", this.#onFrameLoad)
        document.removeEventListener("keydown", this.#onGlobalKeydown)
    }

    search() {
        const query = this.inputTarget.value.trim()
        if (!query) {
            this.#clear()
            return
        }
        this.#showClear(true)
        this.#filterAndHighlight(query)
    }

    onKeydown(event) {
        if (event.key === "ArrowDown" || (event.key === "Enter" && !event.shiftKey)) {
            event.preventDefault()
            this.#navigate(1)
        } else if (event.key === "ArrowUp" || (event.key === "Enter" && event.shiftKey)) {
            event.preventDefault()
            this.#navigate(-1)
        } else if (event.key === "Escape") {
            event.preventDefault()
            this.inputTarget.value = ""
            this.#clear()
            this.inputTarget.blur()
        }
    }

    clearSearch() {
        this.inputTarget.value = ""
        this.#clear()
        this.inputTarget.focus()
    }

    focusSearch() {
        this.inputTarget.focus()
        this.inputTarget.select()
    }

    // --- Private ---

    // The stream reloads on its own — a note locking, a note changing date — and
    // takes the filtered-away cards and the highlights with it, so a standing
    // query has to be applied again. Landing on the first match belongs to
    // typing a query; keepPlace leaves the reader where the reload found them.
    #onFrameLoad = () => {
        const query = this.inputTarget.value.trim()
        if (query) {
            this.#filterAndHighlight(query, { keepPlace: true })
        }
    }

    #onGlobalKeydown = (e) => {
        if ((e.metaKey || e.ctrlKey) && e.key === "f") {
            e.preventDefault()
            this.inputTarget.focus()
            this.inputTarget.select()
        }
    }

    #clear() {
        this.#matches = []
        this.#currentIndex = -1
        this.#clearHighlights()
        this.#showClear(false)

        const stream = this.element.querySelector("#notes-stream")
        if (!stream) return
        for (const el of stream.children) {
            el.style.display = ""
        }

        this.element.querySelectorAll(".note-search-current").forEach(el => {
            el.classList.remove("note-search-current")
        })

        this.#updateCount()
    }

    #clearHighlights() {
        if (this.#highlight) this.#highlight.clear()
    }

    #showClear(show) {
        if (!this.hasClearBtnTarget) return
        this.clearBtnTarget.style.display = show ? "" : "none"
    }

    #filterAndHighlight(query, { keepPlace = false } = {}) {
        const currentId = keepPlace ? this.#matches[this.#currentIndex]?.id : null
        this.#matches = []
        this.#currentIndex = -1
        this.#clearHighlights()

        // Remove previous current-match styling
        this.element.querySelectorAll(".note-search-current").forEach(el => {
            el.classList.remove("note-search-current")
        })

        const stream = this.element.querySelector("#notes-stream")
        if (!stream) return

        const lowerQuery = query.toLowerCase()
        let currentSeparator = null
        let separatorHasVisibleNote = false

        for (const child of stream.children) {
            const isNoteCard = child.hasAttribute("data-controller") &&
                child.getAttribute("data-controller").includes("note-card")

            if (!isNoteCard) {
                // Resolve previous separator visibility
                if (currentSeparator) {
                    currentSeparator.style.display = separatorHasVisibleNote ? "" : "none"
                }
                currentSeparator = child
                separatorHasVisibleNote = false
                continue
            }

            const state = child.getAttribute("data-note-card-state-value")
            if (state === "placeholder") {
                child.style.display = "none"
                continue
            }

            const text = this.#getNoteText(child)
            if (text.toLowerCase().includes(lowerQuery)) {
                child.style.display = ""
                separatorHasVisibleNote = true
                this.#matches.push(child)

                const ranges = this.#findTextRanges(child, lowerQuery)
                for (const range of ranges) {
                    this.#highlight?.add(range)
                }
            } else {
                child.style.display = "none"
            }
        }

        // Resolve last separator
        if (currentSeparator) {
            currentSeparator.style.display = separatorHasVisibleNote ? "" : "none"
        }

        if (this.#matches.length > 0) {
            const kept = currentId ? this.#matches.findIndex((el) => el.id === currentId) : -1
            this.#currentIndex = kept === -1 ? 0 : kept
            const current = this.#matches[this.#currentIndex]
            current.classList.add("note-search-current")
            if (!keepPlace) {
                current.scrollIntoView({ behavior: "smooth", block: "center" })
            }
        }

        this.#updateCount()
    }

    #getNoteText(noteEl) {
        const display = noteEl.querySelector("[data-note-card-target='display']:not(.hidden)")
        if (display) return display.textContent || ""

        const pm = noteEl.querySelector(".ProseMirror")
        if (pm) return pm.textContent || ""

        return ""
    }

    #findTextRanges(container, query) {
        const ranges = []
        const searchIn = container.querySelector("[data-note-card-target='display']:not(.hidden)") ||
            container.querySelector(".ProseMirror") ||
            container

        const walker = document.createTreeWalker(searchIn, NodeFilter.SHOW_TEXT)
        while (walker.nextNode()) {
            const node = walker.currentNode
            const text = node.textContent.toLowerCase()
            let pos = 0
            while ((pos = text.indexOf(query, pos)) !== -1) {
                const range = new Range()
                range.setStart(node, pos)
                range.setEnd(node, pos + query.length)
                ranges.push(range)
                pos += query.length
            }
        }
        return ranges
    }

    #navigate(direction) {
        if (this.#matches.length === 0) return

        // Remove previous styling
        if (this.#currentIndex >= 0 && this.#currentIndex < this.#matches.length) {
            this.#matches[this.#currentIndex].classList.remove("note-search-current")
        }

        this.#currentIndex += direction
        if (this.#currentIndex >= this.#matches.length) this.#currentIndex = 0
        if (this.#currentIndex < 0) this.#currentIndex = this.#matches.length - 1

        this.#matches[this.#currentIndex].classList.add("note-search-current")
        this.#matches[this.#currentIndex].scrollIntoView({ behavior: "smooth", block: "center" })
        this.#updateCount()
    }

    #updateCount() {
        if (!this.hasCountTarget) return
        const query = this.inputTarget.value.trim()
        if (!query) {
            this.countTarget.textContent = ""
        } else if (this.#matches.length === 0) {
            this.countTarget.textContent = "No results"
        } else {
            this.countTarget.textContent = `${this.#currentIndex + 1} / ${this.#matches.length}`
        }
    }
}
